/**
 * Client-side email template renderer for admin preview.
 *
 * These templates mirror the ones in supabase/functions/send-email/index.ts.
 * If the Edge Function templates change, update these too.
 */

const SITE_URL = import.meta.env.VITE_APP_URL || 'http://localhost:5173';

// Brand constants — must match send-email Edge Function
const BRAND = {
  blue: '#2C98D4',
  darkBlue: '#054C95',
  cream: '#F7F5F0',
  creamDark: '#EDEAE4',
  textPrimary: '#2C2C2C',
  textBody: '#6B6B6B',
  textMuted: '#999999',
  white: '#FFFFFF',
  headingFont: "Georgia, 'Times New Roman', serif",
  bodyFont: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

/** Outlook-safe primary button (bulletproof button technique). */
function primaryButton(href: string, text: string): string {
  return `<table role="presentation" cellpadding="0" cellspacing="0">
      <tr>
        <td align="center" style="background-color: ${BRAND.blue}; border-radius: 50px;">
          <a href="${href}" target="_blank" style="background-color: ${BRAND.blue}; border: 1px solid ${BRAND.blue}; border-radius: 50px; color: ${BRAND.white}; display: inline-block; font-family: ${BRAND.bodyFont}; font-size: 15px; font-weight: 600; padding: 14px 36px; text-decoration: none; mso-padding-alt: 0;">
            ${text}
          </a>
        </td>
      </tr>
    </table>`;
}

/** Outlook-safe outline button (bulletproof button technique). */
function outlineButton(href: string, text: string): string {
  return `<table role="presentation" cellpadding="0" cellspacing="0">
      <tr>
        <td align="center" style="border-radius: 50px;">
          <a href="${href}" target="_blank" style="border: 2px solid ${BRAND.blue}; border-radius: 50px; color: ${BRAND.blue}; display: inline-block; font-family: ${BRAND.bodyFont}; font-size: 14px; font-weight: 600; padding: 12px 32px; text-decoration: none; mso-padding-alt: 0;">
            ${text}
          </a>
        </td>
      </tr>
    </table>`;
}

/** Wrap body content with the Ellie branded email shell (header + footer). */
function wrapEmail(title: string, bodyContent: string): string {
  const logoUrl = `${SITE_URL}/images/logo-white.svg`;

  return `<!DOCTYPE html>
<html lang="en" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <!--[if gte mso 9]>
  <xml>
    <o:OfficeDocumentSettings>
      <o:AllowPNG/>
      <o:PixelsPerInch>96</o:PixelsPerInch>
    </o:OfficeDocumentSettings>
  </xml>
  <![endif]-->
</head>
<body style="margin: 0; padding: 0; background-color: ${BRAND.cream}; font-family: ${BRAND.bodyFont};">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color: ${BRAND.cream};">
    <tr>
      <td align="center" style="padding: 40px 20px;">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width: 600px; width: 100%;">
          <!-- Header -->
          <tr>
            <td style="background-color: ${BRAND.darkBlue}; background-image: linear-gradient(135deg, ${BRAND.darkBlue} 0%, ${BRAND.blue} 100%); padding: 36px 40px; border-radius: 14px 14px 0 0; text-align: center;">
              <!--[if mso]>
              <v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="width:600px;height:100px;">
              <v:fill type="gradient" color="${BRAND.darkBlue}" color2="${BRAND.blue}" angle="135" />
              <v:textbox inset="0,0,0,0" style="mso-fit-shape-to-text:true">
              <![endif]-->
              <img src="${logoUrl}" alt="Ellie" height="36" style="display: block; margin: 0 auto; height: 36px; width: auto;" />
              <!--[if mso]>
              <p style="margin: 10px 0 0; font-size: 13px; font-family: ${BRAND.bodyFont}; color: ${BRAND.white}; letter-spacing: 0.5px;">
              <![endif]-->
              <!--[if !mso]><!-->
              <p style="margin: 10px 0 0; font-size: 13px; font-family: ${BRAND.bodyFont}; color: rgba(255, 255, 255, 0.7); letter-spacing: 0.5px;">
              <!--<![endif]-->
                Your personalized language workbook
              </p>
              <!--[if mso]></v:textbox></v:rect><![endif]-->
            </td>
          </tr>
          <!-- Body -->
          <tr>
            <td style="background-color: ${BRAND.white}; padding: 44px 40px 40px;">
              ${bodyContent}
            </td>
          </tr>
          <!-- Footer -->
          <tr>
            <td style="padding: 28px 40px; text-align: center;">
              <p style="margin: 0; font-size: 12px; color: ${BRAND.textMuted}; font-family: ${BRAND.bodyFont};">
                &copy; ${new Date().getFullYear()} Ellie Language. All rights reserved.
              </p>
              <p style="margin: 8px 0 0; font-size: 12px; color: ${BRAND.textMuted}; font-family: ${BRAND.bodyFont};">
                <a href="${SITE_URL}" style="color: ${BRAND.blue}; text-decoration: none;">get-ellie.com</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

/** Render the "subscription_confirmation" email template — sent after subscription purchase. */
export function renderSubscriptionConfirmationEmail(
  displayName: string,
  paceName = 'Regular',
): string {
  const subject = 'Welcome to your Ellie subscription!';
  const portalUrl = '#portal-preview';
  const accountUrl = `${SITE_URL}/account`;
  const orderId = 'PREVIEW0';

  return wrapEmail(
    subject,
    `
    <h2 style="margin: 0 0 20px; font-size: 24px; font-family: ${BRAND.headingFont}; color: ${BRAND.textPrimary}; font-weight: 600;">
      Welcome aboard, ${displayName}!
    </h2>
    <p style="margin: 0 0 16px; font-size: 16px; color: ${BRAND.textBody}; line-height: 1.7; font-family: ${BRAND.bodyFont};">
      You've subscribed to the <strong style="color: ${BRAND.textPrimary};">${paceName}</strong> plan.
      Every month we'll generate a brand-new personalized workbook tailored to your interests and level.
    </p>
    <p style="margin: 0 0 28px; font-size: 16px; color: ${BRAND.textBody}; line-height: 1.7; font-family: ${BRAND.bodyFont};">
      Your first workbook is already being created — we'll email you again once it's ready to download.
    </p>
    <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="margin: 0 0 28px;">
      <tr>
        <td style="background-color: ${BRAND.cream}; padding: 18px 22px; border-radius: 10px; border-left: 3px solid ${BRAND.blue};">
          <table role="presentation" cellpadding="0" cellspacing="0" width="100%">
            <tr>
              <td style="padding-bottom: 10px;">
                <p style="margin: 0; font-size: 12px; color: ${BRAND.textMuted}; text-transform: uppercase; letter-spacing: 0.5px; font-family: ${BRAND.bodyFont};">Plan</p>
                <p style="margin: 4px 0 0; font-size: 16px; color: ${BRAND.textPrimary}; font-weight: 600; font-family: ${BRAND.bodyFont};">
                  ${paceName}
                </p>
              </td>
            </tr>
            <tr>
              <td style="padding-bottom: 10px;">
                <p style="margin: 0; font-size: 12px; color: ${BRAND.textMuted}; text-transform: uppercase; letter-spacing: 0.5px; font-family: ${BRAND.bodyFont};">Billing cycle</p>
                <p style="margin: 4px 0 0; font-size: 16px; color: ${BRAND.textPrimary}; font-weight: 600; font-family: ${BRAND.bodyFont};">
                  Monthly
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <p style="margin: 0; font-size: 12px; color: ${BRAND.textMuted}; text-transform: uppercase; letter-spacing: 0.5px; font-family: ${BRAND.bodyFont};">Order reference</p>
                <p style="margin: 4px 0 0; font-size: 16px; color: ${BRAND.textPrimary}; font-family: monospace; font-weight: 600;">
                  ${orderId}
                </p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
    <div style="margin: 0 0 16px;">
    ${primaryButton(portalUrl, 'Manage your subscription')}
    </div>
    <div style="margin: 0 0 28px;">
    ${outlineButton(accountUrl, 'View your account')}
    </div>
    <p style="margin: 0; font-size: 14px; color: ${BRAND.textMuted}; font-family: ${BRAND.bodyFont};">
      If you have any questions, just reply to this email.
    </p>
    `,
  );
}

/** Render the "workbook_ready" email template — the one sent on delivery. */
export function renderWorkbookReadyEmail(displayName: string): string {
  const subject = 'Your Ellie workbook is ready to download!';
  // Use placeholder URLs — the real ones are generated server-side during delivery
  const downloadUrl = '#download-preview';
  const accountUrl = `${SITE_URL}/account`;

  return wrapEmail(
    subject,
    `
    <h2 style="margin: 0 0 20px; font-size: 24px; font-family: ${BRAND.headingFont}; color: ${BRAND.textPrimary}; font-weight: 600;">
      Your workbook is ready, ${displayName}!
    </h2>
    <p style="margin: 0 0 24px; font-size: 16px; color: ${BRAND.textBody}; line-height: 1.7; font-family: ${BRAND.bodyFont};">
      Great news — your personalized language workbook has been reviewed and is ready for you.
    </p>
    <div style="margin: 0 0 16px;">
    ${primaryButton(downloadUrl, 'Download your workbook')}
    </div>
    <div style="margin: 0 0 28px;">
    ${outlineButton(accountUrl, 'Go to your account')}
    </div>
    <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="margin: 0 0 28px;">
      <tr>
        <td style="background-color: ${BRAND.cream}; padding: 16px 20px; border-radius: 10px; text-align: center;">
          <p style="margin: 0; font-size: 13px; color: ${BRAND.textBody}; line-height: 1.6; font-family: ${BRAND.bodyFont};">
            This download link is valid for 7 days.<br />
            You can always access your workbook from your account page.
          </p>
        </td>
      </tr>
    </table>
    <p style="margin: 0; font-size: 14px; color: ${BRAND.textMuted}; font-family: ${BRAND.bodyFont};">
      If you have any questions, just reply to this email.
    </p>
    `,
  );
}
